import {
  LeadValue,
  TenantCommercial,
  useAsyncOperation,
  useLeads,
} from "@inrange/building-manager-api-client";
import { useEffect, useRef, useState } from "react";

// Settles the rows a list is showing as "Calculating".
//
// The list endpoints derive the commercial view from cache on read and never
// fetch, so without this a pending row would sit on "Calculating" forever - the
// exact failure that made showing the state worth doing in the first place. One
// LRO for the whole page plus one poll, never one request per row: the API
// Lambda has no concurrency cap, and a per-row fan-out is what drains the shared
// DB pool and breaks other surfaces.
//
// Shared by /leads and /leads/search so the two cannot drift on when they
// resolve, how long they poll, or when they give up.
//
// It keeps firing rounds WHILE THEY ARE MAKING PROGRESS, up to MAX_SETTLE_ROUNDS.
// One round is bounded server-side (the operation's result has to fit inside a
// DynamoDB record), so a page with more pending rows than that cap cannot settle
// in one - and stopping after one round left the rest reading "Calculating" until
// the operator happened to reload, several times. A round that settles NOTHING
// stops the loop: the upstream is down rather than slow, and re-firing then would
// spin against a dead API while spending a per-token Companies House budget and
// billed Places calls. Those rows keep their "Calculating" label, which stays
// true, and a fresh attempt fires the next time the page mounts.
//
// `recount` is the deliberate exception to the progress rule: an operator who
// corrects a tenant's domain has changed the INPUT, so the cached answer is now
// wrong rather than merely missing, and nothing else would ever re-ask. It goes
// through this same hook rather than a second kickoff-and-poll implementation, so
// the two cannot drift on how they fire, poll, or hand back results.

// Enough to settle a large page through a server-side cap of ~120 rows a round,
// while bounding what a pathological page can spend.
const MAX_SETTLE_ROUNDS = 6;

// What the operation hands back per row: ONLY the two fields it can change.
// Deliberately not the refreshed row - the operation's result is stored in a
// DynamoDB record with a hard 400KB item limit, and returning whole rows blew
// through it on an ordinary page, failing the operation so nothing ever settled.
export interface CommercialResolveRow {
  tenant: TenantCommercial | null;
  value: LeadValue;
}

interface Params {
  // Rows currently showing as pending. Pass one or the other: /leads keys by
  // lead id, /leads/search keys VOA rows by ref.
  leadIds?: string[];
  sourceRefs?: string[];
  // Called when the lookups have landed. `commercial` is keyed by sourceRef when
  // `sourceRefs` were passed (the search page holds its rows in local state and
  // merges these two fields in place); it is empty for the inbox, which re-reads
  // its own list query instead.
  onSettled: (commercial: Record<string, CommercialResolveRow>) => void;
  enabled?: boolean;
}

interface Recountable {
  leadIds?: string[];
  sourceRefs?: string[];
}

export const useSettleCommercialPending = ({
  leadIds,
  sourceRefs,
  onSettled,
  enabled = true,
}: Params): {
  resolving: boolean;
  settled: boolean;
  recount: (rows: Recountable) => void;
} => {
  const { resolveLeadCommercial } = useLeads();
  const [operationKey, setOperationKey] = useState<string | null>(null);
  const [siteId, setSiteId] = useState<string>("");
  const [settled, setSettled] = useState(false);
  // Refs, not state, so firing a round cannot itself re-render and re-trigger the
  // effect that fired it.
  const roundsFired = useRef(0);
  const pendingAtLastRound = useRef<number | null>(null);
  // Kept in a ref so an inline arrow from the caller does not re-run the
  // completion effect on every render of the page.
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  const pendingCount =
    (leadIds?.length ?? 0) + (sourceRefs?.length ?? 0);
  const inFlight = operationKey !== null || resolveLeadCommercial.isLoading;

  useEffect(() => {
    if (!enabled || pendingCount === 0 || inFlight) return;
    if (roundsFired.current >= MAX_SETTLE_ROUNDS) return;
    // Keep going only while each round is actually shrinking the pending set.
    // One round is capped server-side (the operation's result has to fit in a
    // DynamoDB record), so a page with more pending rows than that cap needs
    // several rounds to converge - but a round that settles NOTHING means the
    // upstream is down, and re-firing then would spin against a dead API while
    // spending a per-token Companies House budget and billed Places calls.
    if (
      pendingAtLastRound.current !== null &&
      pendingCount >= pendingAtLastRound.current
    ) {
      return;
    }
    roundsFired.current += 1;
    pendingAtLastRound.current = pendingCount;
    setSettled(false);
    resolveLeadCommercial.mutate(
      { leadIds, sourceRefs },
      {
        onSuccess: (data) => {
          setOperationKey(data.operationKey);
          setSiteId(data.siteId);
        },
        // A failed kickoff is not worth a banner: the bands are an enrichment,
        // not the row itself, and the page is fully usable without them. The
        // next mount tries again.
        onError: () => setSettled(true),
      }
    );
  }, [
    enabled,
    pendingCount,
    inFlight,
    leadIds,
    sourceRefs,
    resolveLeadCommercial,
  ]);

  // Explicit re-ask for specific rows, whatever the page's pending set is: the
  // row an operator just corrected is usually NOT pending (it had an answer, and
  // that answer is what they are replacing).
  const recount = (rows: Recountable) => {
    setSettled(false);
    resolveLeadCommercial.mutate(rows, {
      onSuccess: (data) => {
        setOperationKey(data.operationKey);
        setSiteId(data.siteId);
      },
      onError: () => setSettled(true),
    });
  };

  const operation = useAsyncOperation({
    siteId,
    operationKey,
    enabled: operationKey !== null,
  });

  const status = operation.data?.status;
  useEffect(() => {
    if (status !== "completed" && status !== "failed") return;
    setSettled(true);
    setOperationKey(null);
    if (status !== "completed") return;
    const result = operation.data?.result as
      | { commercial?: Record<string, CommercialResolveRow> }
      | undefined;
    onSettledRef.current(result?.commercial ?? {});
  }, [status, operation.data]);

  return {
    resolving: !settled && (resolveLeadCommercial.isLoading || operation.isPolling),
    settled,
    recount,
  };
};
